import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {Object} AutocompleteSelection
 * @property {string} [id]
 * @property {string} [name]
 * @property {string} [label]
 * @property {string} [value]
 * @property {Object} [raw]
 */

/**
 * @typedef {Object} AutocompleteRef
 * @property {DOMStringMap} dataset
 * @property {{ input?: HTMLInputElement }} [refs]
 * @property {(data?: { programId?: string, tags?: string }) => void} [setProgram]
 * @property {() => void} [prefetch]
 * @property {() => AutocompleteSelection | null} [getSelection]
 * @property {() => boolean} [hasSelection]
 */

/**
 * @typedef {Object} VipGroupRule
 * @property {string} [vipGroupId]
 * @property {string[]} required
 * @property {string[]} optional
 * @property {boolean} [allowFreeOrganization]
 * @property {string} [tags]
 */

/**
 * @typedef {Object} VipGroupConfig
 * @property {string} key
 * @property {string} [programId]
 * @property {string} [heading]
 * @property {string} [subheading]
 * @property {string} [fileStepSubheading]
 * @property {string} [fileStepDocuments]
 * @property {string} [howItWorksLabel]
 * @property {string} [howItWorksTitle]
 * @property {string} [howItWorksText]
 * @property {boolean} [hasOnlyOneOrganization]
 * @property {string} [companyName]
 * @property {string} [companyId]
 */

/**
 * @typedef {Object} VipConfigPayload
 * @property {VipGroupConfig[]} [blocks]
 * @property {string} [submissionEndpoint]
 * @property {string} [organizationLookupEndpoint]
 * @property {string} [continueLabel]
 * @property {string} [submitStepTwoLabel]
 * @property {string} [backLabel]
 */

/**
 * @typedef {Object} VipApplicationRefs
 * @property {DialogComponent} dialogComponent
 * @property {DialogComponent} successDialogComponent
 * @property {HTMLScriptElement} groupsData
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} groupKey
 * @property {HTMLElement} header
 * @property {HTMLElement} headingText
 * @property {HTMLElement} subheadingText
 * @property {HTMLElement} fileStepDocuments
 * @property {HTMLElement} howItWorksLabel
 * @property {HTMLElement} howItWorksTitle
 * @property {HTMLElement} howItWorksText
 * @property {HTMLElement} statusMessage
 * @property {HTMLElement} statusTitle
 * @property {HTMLElement} statusText
 * @property {HTMLElement} fieldsContainer
 * @property {HTMLButtonElement} backButton
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} submitDefaultText
 * @property {AutocompleteRef} [branchAutocomplete]
 * @property {AutocompleteRef} organizationAutocomplete
 * @property {HTMLInputElement} email
 * @property {HTMLInputElement} firstName
 * @property {HTMLInputElement} lastName
 * @property {HTMLInputElement} dateOfBirth
 * @property {HTMLInputElement} dischargeDate
 * @property {HTMLInputElement} fileUpload
 * @property {HTMLInputElement} [postalCode]
 * @property {HTMLInputElement} [militaryStatus]
 */

/** @type {Record<string, VipGroupRule>} */
const GROUP_RULES = {
  'active-duty': {
    vipGroupId: 'military_active',
    required: ['military_status', 'branch_of_service', 'date_of_birth', 'file_upload'],
    optional: [],
  },
  'active-duty-retired-military-retired': {
    vipGroupId: 'military_active',
    required: ['branch_of_service', 'date_of_birth', 'discharge_date', 'file_upload'],
    optional: [],
    tags: 'VETERAN',
  },
  'law-enforcement': {
    vipGroupId: 'law_enforcement',
    required: ['organization', 'date_of_birth', 'file_upload'],
    optional: [],
    allowFreeOrganization: true,
  },
  'retired-law-enforcement': {
    vipGroupId: 'law_enforcement_retired',
    required: ['organization', 'file_upload'],
    optional: [],
  },
  'federal-employee': {
    vipGroupId: 'federal_employee',
    required: ['organization', 'file_upload'],
    optional: [],
  },
  'guide-outfitter': {
    vipGroupId: 'guide_outfitter',
    required: ['organization', 'file_upload'],
    optional: ['business_name', 'business_website'],
  },
  'tac-shooting-instructors': {
    vipGroupId: 'tac_shooting_instructors',
    required: ['organization', 'file_upload'],
    optional: ['business_name', 'business_website'],
  },
  'hunter-ed-instructor': {
    vipGroupId: 'hunter_ed_instructor',
    required: ['organization', 'file_upload'],
    optional: [],
  },
  'bass-cabelas-employee': {
    vipGroupId: 'bass_cabelas_employee',
    required: ['organization', 'file_upload'],
    optional: [],
  },
  'scheels-employees': {
    vipGroupId: 'scheels_employees',
    required: ['organization', 'file_upload'],
    optional: [],
  },
  'shooting-industry': {
    vipGroupId: 'shooting_industry',
    required: ['organization', 'file_upload'],
    optional: ['business_name', 'business_website'],
  },
  'custom-gun-maker': {
    vipGroupId: 'custom_gun_maker',
    required: ['organization', 'file_upload'],
    optional: ['business_name', 'business_website'],
  }
};

const DEFAULT_ORGANIZATION_LOOKUP_ENDPOINT = '/api/sheerid/organizations';
const DEFAULT_SUBMISSION_ENDPOINT = '/api/sheerid/submit';
const MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'pdf'];
const GROUP_RULE_FALLBACK = { required: ['file_upload'], optional: [] };
/** @type {Record<string, string>} */
const LAW_ENFORCEMENT_STATUS_BY_GROUP = {
  'law-enforcement': 'Police',
  'retired-law-enforcement': 'Police',
};
/** @type {Record<string, string>} */
const MILITARY_STATUS_LABELS = {
  ACTIVE_DUTY: 'Active Duty',
  RESERVIST: 'Reservist or National Guard',
};

/**
 * Multi-step VIP application form controller.
 */
class VipApplicationForm extends Component {
  /** @type {VipApplicationRefs} */
  refs;

  /** @type {string[]} */
  requiredRefs = ['dialogComponent', 'successDialogComponent', 'groupsData'];

  /** @type {string[]} */
  #requiredNestedRefs = [
    'form',
    'groupKey',
    'header',
    'headingText',
    'subheadingText',
    'fileStepDocuments',
    'howItWorksLabel',
    'howItWorksTitle',
    'howItWorksText',
    'statusMessage',
    'statusTitle',
    'statusText',
    'fieldsContainer',
    'backButton',
    'submitButton',
    'submitDefaultText',
    'organizationAutocomplete',
    'email',
    'firstName',
    'lastName',
    'dateOfBirth',
    'dischargeDate',
    'fileUpload',
  ];

  #groups = [];
  #activeGroup = null;
  #submissionEndpoint = '';
  #organizationLookupEndpoint = '';
  #customerId = '';
  #defaultHowItWorksLabel = 'How does verifying work?';
  #defaultHowItWorksTitle = 'How it works';
  #defaultHowItWorksText = 'Verification is completed through SheerID. Some groups are automatically verified, while others require manual review.';
  // Captured from the SSR modal markup (section default_heading / default_subheading) so a
  // block with an empty heading/subheading falls back to the section default instead of blank.
  #defaultHeading = '';
  #defaultSubheading = '';
  #defaultFileStepSubheading = 'Please upload a document to verify your eligibility for the [VIP Program Name] program.';
  #continueLabel = 'Continue';
  #submitStepTwoLabel = 'Submit application';
  #isLoggedIn = false;
  #currentStep = 'details';
  #requiresFileStep = false;
  #allowFreeOrganization = false;
  #visibleStepOneFields = new Set();
  #requiredStepOneFields = new Set();
  /** @type {AbortController | undefined} */
  #militaryStatusAbortController;
  /** @type {AbortController | undefined} */
  #dischargeDateAbortController;
  /** @type {AbortController | undefined} */
  #dateOfBirthAbortController;

  /**
   * Initializes refs and form state on mount.
   *
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.#mergeNestedRefs();
    this.#hydrateConfig();
    this.#hydrateCustomer();
    this.#setGlobalRequiredState(false);
    this.#bindMilitaryStatusListener();
    this.#bindDischargeDateMaskListener();
  }

  /**
   * Re-merges refs after section re-render updates.
   *
   * @returns {void}
   */
  updatedCallback() {
    super.updatedCallback();
    this.#mergeNestedRefs();
    this.#bindMilitaryStatusListener();
    this.#bindDischargeDateMaskListener();
  }

  /**
   * Disconnects the change listener when the component is removed.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#militaryStatusAbortController?.abort();
    this.#militaryStatusAbortController = undefined;
    this.#dischargeDateAbortController?.abort();
    this.#dischargeDateAbortController = undefined;
    this.#dateOfBirthAbortController?.abort();
    this.#dateOfBirthAbortController = undefined;
  }

  /**
   * Binds the change listener to the current militaryStatus ref. Aborts any
   * previous binding so a section morph can't leave a stale listener on a
   * detached input.
   *
   * @returns {void}
   */
  #bindMilitaryStatusListener() {
    this.#militaryStatusAbortController?.abort();
    const target = this.refs.militaryStatus;
    if (!target) {
      this.#militaryStatusAbortController = undefined;
      return;
    }
    this.#militaryStatusAbortController = new AbortController();
    target.addEventListener('change', this.#handleMilitaryStatusChange, {
      signal: this.#militaryStatusAbortController.signal,
    });
  }

  /**
   * Binds the MM/YYYY input mask to the discharge date field.
   *
   * @returns {void}
   */
  #bindDischargeDateMaskListener() {
    this.#dischargeDateAbortController?.abort();
    const target = this.refs.dischargeDate;
    if (!target) {
      this.#dischargeDateAbortController = undefined;
      return;
    }

    this.#dischargeDateAbortController = new AbortController();
    target.addEventListener('input', this.#handleDischargeDateInput, {
      signal: this.#dischargeDateAbortController.signal,
    });
  }

  /**
   * Keeps discharge date input formatted as `MM/YYYY` while typing.
   *
   * @param {Event} event
   * @returns {void}
   */
  #handleDischargeDateInput = (event) => {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;

    const previousValue = input.value;
    const previousCursor = input.selectionStart ?? previousValue.length;
    const digitsBeforeCursor = previousValue.slice(0, previousCursor).replace(/\D/g, '').length;
    const formattedValue = this.#formatMonthYearInput(previousValue);

    if (formattedValue === previousValue) return;

    input.value = formattedValue;
    const cursor = this.#resolveMaskCursor(formattedValue, digitsBeforeCursor);
    input.setSelectionRange(cursor, cursor);
  };

  /**
   * Keeps date of birth input formatted as `MM/DD/YYYY` while typing.
   *
   * @param {Event} event
   * @returns {void}
   */
  #handleDateOfBirthInput = (event) => {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;

    const previousValue = input.value;
    const previousCursor = input.selectionStart ?? previousValue.length;
    const digitsBeforeCursor = previousValue.slice(0, previousCursor).replace(/\D/g, '').length;
    const formattedValue = this.#formatDateInput(previousValue);

    if (formattedValue === previousValue) return;

    input.value = formattedValue;
    const cursor = this.#resolveMaskCursor(formattedValue, digitsBeforeCursor);
    input.setSelectionRange(cursor, cursor);
  };

  /**
   * Merges nested refs from the modal subtree into component refs.
   *
   * @returns {void}
   */
  #mergeNestedRefs() {
    const mergedRefs = { ...this.refs };
    const nestedRefs = this.refs.dialogComponent?.querySelectorAll?.('[ref]') || [];

    for (const element of nestedRefs) {
      const refName = element.getAttribute('ref') || '';
      if (!refName || refName in mergedRefs) continue;
      mergedRefs[refName] = element;
    }

    for (const ref of this.#requiredNestedRefs) {
      if (!(ref in mergedRefs)) {
        throw new Error(`Required ref "${ref}" not found in component ${this.tagName.toLowerCase()}`);
      }
    }

    this.refs = mergedRefs;
  }

  /**
   * Reads configuration JSON embedded in the section markup.
   *
   * @returns {void}
   */
  #hydrateConfig() {
    try {
      /** @type {VipConfigPayload} */
      const data = JSON.parse(this.refs.groupsData.textContent || '{}');
      this.#groups = data.blocks || [];
      this.#submissionEndpoint = data.submissionEndpoint || DEFAULT_SUBMISSION_ENDPOINT;
      this.#organizationLookupEndpoint = data.organizationLookupEndpoint || DEFAULT_ORGANIZATION_LOOKUP_ENDPOINT;
      this.#continueLabel = data.continueLabel || this.#continueLabel;
      this.#submitStepTwoLabel = data.submitStepTwoLabel || this.#submitStepTwoLabel;

      // Capture the section defaults from the SSR markup before any group overwrites them.
      this.#defaultHeading = this.refs.headingText?.textContent?.trim() || '';
      this.#defaultSubheading = this.refs.subheadingText?.textContent?.trim() || '';

      this.refs.howItWorksLabel.textContent = this.#defaultHowItWorksLabel;
      this.refs.howItWorksTitle.textContent = this.#defaultHowItWorksTitle;
      this.refs.howItWorksText.textContent = this.#defaultHowItWorksText;
    } catch (error) {
      console.error('vip-application-form: invalid config JSON', error);
    }
  }

  /**
   * Hydrates customer identity fields and auth-dependent state.
   *
   * @returns {void}
   */
  #hydrateCustomer() {
    this.#isLoggedIn = this.dataset.customerLoggedIn === 'true';
    this.#customerId = this.dataset.customerId || '';

    this.refs.firstName.value = this.dataset.customerFirstName || '';
    this.refs.lastName.value = this.dataset.customerLastName || '';
    this.refs.email.value = this.dataset.customerEmail || '';
  }

  /**
   * Opens the application modal for the selected VIP group.
   *
   * @param {string} groupKey
   * @param {Event} [event]
   * @returns {void}
   */
  showByKey = (groupKey, event) => {
    event?.preventDefault();

    const group = this.#groups.find((item) => item.key === groupKey);
    if (!group) {
      this.#setStatus('This VIP group is not configured yet.', true);
      return;
    }

    this.#activeGroup = group;
    this.refs.form.reset();
    this.refs.militaryStatus?.closest('select-field')?.dispatchEvent(new CustomEvent('field:reset'));
    this.#hydrateCustomer();

    this.refs.groupKey.value = group.key || '';
    this.refs.headingText.textContent = group.heading || this.#defaultHeading || group.key || 'VIP application';
    this.refs.subheadingText.textContent = group.subheading || this.#defaultSubheading || '';
    this.#syncOrganizationAutocomplete(group.key || '');
    this.refs.fileStepDocuments.innerHTML = group.fileStepDocuments || '';
    this.#setHidden(this.refs.fileStepDocuments, true);
    this.refs.howItWorksLabel.textContent = group.howItWorksLabel || this.#defaultHowItWorksLabel;
    this.refs.howItWorksTitle.textContent = group.howItWorksTitle || this.#defaultHowItWorksTitle;
    this.refs.howItWorksText.innerHTML = group.howItWorksText || this.#defaultHowItWorksText;

    this.#setStatus('', false);
    this.#clearErrors();
    this.#applyGroupRules(group);
    this.#setUnauthenticatedView(!this.#isLoggedIn);

    this.refs.successDialogComponent.closeDialog();
    this.refs.dialogComponent.showDialog();
  };

  /**
   * Closes success confirmation modal.
   *
   * @param {Event} [event]
   * @returns {void}
   */
  closeSuccessModal = (event) => {
    event?.preventDefault();
    this.refs.successDialogComponent.closeDialog();
  };

  /**
   * Hides full modal content for unauthenticated users and shows sign-in message only.
   *
   * @param {boolean} state
   * @returns {void}
   */
  #setUnauthenticatedView(state) {
    this.#setHidden(this.refs.form, state);

    if (state) {
      this.#setHidden(this.refs.fileStepDocuments, true);
      return;
    }

    this.#setStatus('', false);
  }

  /**
   * Closes the active VIP modal.
   *
   * @param {Event} [event]
   * @returns {void}
   */
  closeModal = (event) => {
    event?.preventDefault();
    this.refs.dialogComponent.closeDialog();
  };

  /**
   * Applies field visibility/requirement rules for selected group.
   *
   * @param {VipGroupConfig} group
   * @returns {void}
   */
  #applyGroupRules(group) {
    /** @type {VipGroupRule} */
    const rule = GROUP_RULES[group.key] || GROUP_RULE_FALLBACK;
    const requiredFields = new Set(rule.required);
    const visibleFields = new Set([...rule.required, ...rule.optional]);

    this.#requiresFileStep = requiredFields.has('file_upload');
    this.#allowFreeOrganization = group.hasOnlyOneOrganization ? false : rule.allowFreeOrganization || false;
    this.#visibleStepOneFields = new Set([...visibleFields].filter((field) => field !== 'file_upload'));
    this.#requiredStepOneFields = new Set([...requiredFields].filter((field) => field !== 'file_upload'));

    if (group.hasOnlyOneOrganization) {
      this.#visibleStepOneFields.delete('organization');
      this.#requiredStepOneFields.delete('organization');
    }

    this.#currentStep = 'details';

    const orgInput = this.refs.organizationAutocomplete?.refs?.input;
    if (orgInput) {
      orgInput.removeEventListener('input', this.#handleOrganizationInput);
      if (this.#allowFreeOrganization) {
        orgInput.addEventListener('input', this.#handleOrganizationInput);
      }
    }

    this.#setHidden(this.refs.form, false);
    this.#setHidden(this.refs.fieldsContainer, false);
    this.#setHidden(this.refs.backButton, true);
    this.#setHidden(this.refs.header, false);

    this.goToDetailsStep();
  }

  /**
   * Activates first step (customer data + group-specific fields).
   *
   * @param {Event} [event]
   * @returns {void}
   */
  goToDetailsStep = (event) => {
    event?.preventDefault();

    this.#currentStep = 'details';
    this.#setHidden(this.refs.backButton, true);
    this.#setHidden(this.refs.fieldsContainer, false);
    this.#setSubmitLabel(this.#continueLabel);
    this.#setStatus('', false);
    this.refs.subheadingText.textContent = this.#activeGroup?.subheading || this.#defaultSubheading || '';
    this.refs.fileStepDocuments.innerHTML = this.#activeGroup?.fileStepDocuments || '';
    this.#setHidden(this.refs.fileStepDocuments, true);
    this.#clearErrors();

    this.#toggleCoreFields(false);
    this.refs.firstName.setAttribute('required', 'required');
    this.refs.lastName.setAttribute('required', 'required');
    this.refs.email.setAttribute('required', 'required');

    for (const field of this.refs.fieldsContainer.querySelectorAll('[data-field]')) {
      const key = field.getAttribute('data-field');
      const input = field.querySelector('input,textarea,select');
      const isVisible = this.#visibleStepOneFields.has(key);

      this.#setHidden(field, !isVisible);
      if (!input) continue;
      if (this.#requiredStepOneFields.has(key)) {
        input.setAttribute('required', 'required');
      } else {
        input.removeAttribute('required');
      }
    }

    this.#syncPostalCodeVisibility();
  };

  /**
   * Shows or hides postal code field depending on whether organization has a free-text value.
   *
   * @returns {void}
   */
  #syncPostalCodeVisibility() {
    if (!this.refs.postalCode) return;
    const postalCodeField = this.refs.postalCode.closest('[data-field]');
    if (!postalCodeField) return;

    const hasSelection = Boolean(this.refs.organizationAutocomplete?.hasSelection?.());
    const show = this.#allowFreeOrganization && !hasSelection;

    this.#setHidden(postalCodeField, !show);
    if (show) {
      this.refs.postalCode.setAttribute('required', 'required');
    } else {
      this.refs.postalCode.removeAttribute('required');
      this.refs.postalCode.value = '';
    }
  }

  /**
   * Reacts to organization autocomplete input changes to sync postal code visibility.
   *
   * @returns {void}
   */
  #handleOrganizationInput = () => {
    requestAnimationFrame(() => this.#syncPostalCodeVisibility());
  };

  /**
   * Activates second step (file upload only).
   *
   * @returns {void}
   */
  #goToFileStep() {
    this.#currentStep = 'file';
    this.#setHidden(this.refs.backButton, false);
    this.#setHidden(this.refs.fieldsContainer, false);
    this.#setSubmitLabel(this.#submitStepTwoLabel);
    this.#setStatus('', false);
    const groupName = this.#activeGroup?.heading || this.#activeGroup?.key || 'VIP Program';
    const fileStepSubheadingTemplate = this.#activeGroup?.fileStepSubheading || this.#defaultFileStepSubheading;
    this.refs.subheadingText.textContent = fileStepSubheadingTemplate.replace('[VIP Program Name]', groupName);
    this.refs.fileStepDocuments.innerHTML = this.#activeGroup?.fileStepDocuments || '';
    this.#setHidden(this.refs.fileStepDocuments, !this.#activeGroup?.fileStepDocuments);
    this.#clearErrors();

    this.#toggleCoreFields(true);
    this.refs.firstName.removeAttribute('required');
    this.refs.lastName.removeAttribute('required');
    this.refs.email.removeAttribute('required');

    for (const field of this.refs.fieldsContainer.querySelectorAll('[data-field]')) {
      const key = field.getAttribute('data-field');
      const input = field.querySelector('input,textarea,select');
      const isFileField = key === 'file_upload';

      this.#setHidden(field, !isFileField);
      if (!input) continue;
      if (isFileField) {
        input.setAttribute('required', 'required');
      } else {
        input.removeAttribute('required');
      }
    }
  }

  /**
   * Hides or shows core profile fields.
   *
   * @param {boolean} hidden
   * @returns {void}
   */
  #toggleCoreFields(hidden) {
    for (const input of [this.refs.firstName, this.refs.lastName, this.refs.email]) {
      const wrapper = input.closest('.field');
      if (wrapper) this.#setHidden(wrapper, hidden);
    }
  }

  /**
   * Sets default required state before group-specific rules are applied.
   *
   * @param {boolean} required
   * @returns {void}
   */
  #setGlobalRequiredState(required) {
    for (const input of this.refs.form.querySelectorAll('input,textarea,select')) {
      if (input.name === 'email') continue;
      if (required) {
        input.setAttribute('required', 'required');
      } else if (!input.closest('[data-field]')) {
        input.removeAttribute('required');
      }
    }
  }

  /**
   * Sets dynamic autocomplete context based on selected VIP group.
   *
   * @param {string} groupKey
   * @returns {void}
   */
  #syncOrganizationAutocomplete(groupKey) {
    if (this.#activeGroup?.hasOnlyOneOrganization) return;

    const programId = this.#activeGroup?.programId || '';
    const tags = this.#resolveAutocompleteTags(groupKey);

    if (this.#organizationLookupEndpoint) {
      this.refs.organizationAutocomplete.dataset.endpoint = this.#organizationLookupEndpoint;
      if (this.refs.branchAutocomplete) {
        this.refs.branchAutocomplete.dataset.endpoint = this.#organizationLookupEndpoint;
      }
    }

    this.refs.organizationAutocomplete.setProgram?.({ programId, tags });
    this.refs.branchAutocomplete?.setProgram?.({ programId, tags });
  }

  /**
   * Resolves the SheerID `tags` filter for the active group. For groups that
   * surface a service-status dropdown, the chosen value drives the filter.
   *
   * @param {string} groupKey
   * @returns {string}
   */
  #resolveAutocompleteTags(groupKey) {
    if (groupKey === 'active-duty') {
      return this.refs.militaryStatus?.value || '';
    }
    return GROUP_RULES[groupKey]?.tags || '';
  }

  /**
   * Re-syncs autocomplete tag filter when the service-status dropdown changes.
   *
   * @returns {void}
   */
  #handleMilitaryStatusChange = () => {
    if (this.#activeGroup?.key !== 'active-duty') return;
    this.#syncOrganizationAutocomplete(this.#activeGroup.key);
  };

  /**
   * Handles multi-step submit flow and final request sending.
   *
   * @param {SubmitEvent} event
   * @returns {Promise<void>}
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.#activeGroup) {
      this.#setStatus('Select a VIP group before submitting.', true);
      return;
    }

    if (!this.#isLoggedIn) {
      return;
    }

    const activeGroupKey = this.#activeGroup?.key || this.refs.groupKey.value || '';
    const groupRule = GROUP_RULES[activeGroupKey];
    const programId = this.#activeGroup?.programId || '';
    if (!programId || !groupRule?.vipGroupId) {
      this.#setStatus('This VIP group is not configured yet.', true);
      return;
    }

    this.#clearErrors();

    if (this.#currentStep === 'details') {
      const isValidStepOne = this.#validate();
      if (!isValidStepOne) {
        return;
      }

      if (this.#requiresFileStep) {
        this.#goToFileStep();
        return;
      }
    }

    const isValid = this.#validate();
    if (!isValid) {
      return;
    }

    this.#setSubmitting(true);

    const formData = this.#buildSubmitPayload(activeGroupKey, groupRule, programId);
    try {
      const response = await fetch(this.#submissionEndpoint, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        this.#applyBackendErrors(payload);
        return;
      }

      this.#setStatus('', false);
      this.refs.dialogComponent.closeDialog();
      this.refs.successDialogComponent.showDialog();
    } catch (error) {
      console.error('vip-application-form: submission failed', error);
      if (this.#isHidden(this.refs.statusMessage)) {
        this.#setStatus('Submission failed. Please try again.', true);
      }
    } finally {
      this.#setSubmitting(false);
    }
  };

  /**
   * Builds payload compatible with /api/sheerid/submit contract.
   *
   * @returns {FormData}
   */
  #buildSubmitPayload(activeGroupKey, groupRule, programId) {
    const formData = new FormData(this.refs.form);
    const vipGroupId = groupRule.vipGroupId;
    const birthDate = this.#normalizeDateForApi(this.refs.dateOfBirth.value || '');
    const dischargeDate = this.#normalizeMonthYearForApi(String(formData.get('discharge_date') || ''));
    const branchSelection = this.refs.branchAutocomplete?.getSelection?.();
    const branchInputFallback = this.refs.form.querySelector('[name="branch_of_service"]');
    const branchName = String(
      branchSelection?.raw?.name ||
        branchSelection?.name ||
        branchSelection?.label ||
        branchSelection?.value ||
        branchInputFallback?.value ||
        formData.get('branch_of_service') ||
        '',
    ).trim();
    const branchId = String(branchSelection?.raw?.id || branchSelection?.id || '').trim();
    const files = [...(this.refs.fileUpload?.files ?? [])];
    const organizationSelection = this.refs.organizationAutocomplete?.getSelection?.();

    formData.set('program_id', programId);
    formData.set('vip_group', vipGroupId);
    formData.set('firstName', this.refs.firstName.value || '');
    formData.set('lastName', this.refs.lastName.value || '');
    formData.set('email', this.refs.email.value || '');
    formData.set('birthDate', birthDate);
    formData.set('customer_id', this.#customerId);
    const militaryStatusCode = String(formData.get('military_status') || '').trim();

    formData.delete('branch_of_service');
    formData.delete('branch');
    formData.delete('organization');
    formData.delete('organization_id');
    formData.delete('statuses');
    formData.delete('statuses[]');
    formData.delete('status');
    formData.delete('military_status');

    const lawEnforcementStatus = LAW_ENFORCEMENT_STATUS_BY_GROUP[activeGroupKey];
    if (lawEnforcementStatus) {
      formData.set('statuses[]', lawEnforcementStatus);
    }

    if (activeGroupKey === 'active-duty') {
      const statusLabel = MILITARY_STATUS_LABELS[militaryStatusCode];
      if (statusLabel) formData.set('status', statusLabel);
    }

    if (activeGroupKey === 'active-duty-retired-military-retired') {
      formData.set('status', 'Military Veteran or Retiree');
    }

    if (dischargeDate) {
      formData.set('dischargeDate', String(dischargeDate));
    }

    if (branchName) formData.set('branch[name]', branchName);
    if (branchId) formData.set('branch[id]', branchId);

    if (this.#activeGroup?.hasOnlyOneOrganization) {
      const companyName = String(this.#activeGroup.companyName || '').trim();
      const companyId = String(this.#activeGroup.companyId || '').trim();
      if (companyName) formData.set('organization[name]', companyName);
      if (companyId) formData.set('organization[id]', companyId);
    } else if (organizationSelection && typeof organizationSelection === 'object') {
      const organizationName =
        String(organizationSelection.raw?.name || organizationSelection.name || organizationSelection.label || organizationSelection.value || '').trim();
      const organizationId = String(organizationSelection.raw?.id || organizationSelection.id || '').trim();

      if (organizationName) formData.set('organization[name]', organizationName);
      if (organizationId) formData.set('organization[id]', organizationId);
    } else if (this.#allowFreeOrganization) {
      const rawOrganizationInput = this.refs.organizationAutocomplete?.refs?.input;
      const organizationName = String(rawOrganizationInput?.value || '').trim();
      if (organizationName) {
        formData.set('organization[name]', organizationName);
        formData.set('organization[id]', '0');
      }
    }

    const postalCode = String(formData.get('postal_code') || '').trim();
    formData.delete('postal_code');
    if (postalCode) formData.set('postalCode', postalCode);

    formData.delete('files[]');
    for (const file of files) {
      formData.append('files[]', file);
    }

    return formData;
  }

  /**
   * Applies API validation errors to field-level UI and status area.
   *
   * @param {unknown} payload
   * @returns {void}
   */
  #applyBackendErrors(payload) {
    const message = payload && typeof payload === 'object' ? String(payload.message || '') : '';
    const errorMap = payload && typeof payload === 'object' && payload.errors && typeof payload.errors === 'object' ? payload.errors : null;

    if (!errorMap) {
      this.#setStatus(message || 'Submission failed. Please try again.', true);
      return;
    }

    this.#clearErrors();
    let firstInvalidField = null;
    const fallbackMessages = [];

    for (const [apiKey, value] of Object.entries(errorMap)) {
      const messages = Array.isArray(value) ? value.map((item) => String(item)) : [String(value)];
      const field = this.#resolveFieldByApiKey(apiKey);

      if (field) {
        this.#setError(field, messages[0] || 'Invalid value.');
        if (!firstInvalidField) firstInvalidField = field;
      } else if (messages[0]) {
        fallbackMessages.push(messages[0]);
      }
    }

    if (firstInvalidField instanceof HTMLElement) {
      firstInvalidField.focus();
    }

    const fallbackText = fallbackMessages.join(' ');
    this.#setStatus(fallbackText || message || 'Submission failed. Please check the form fields.', true);
  }

  /**
   * Maps backend error keys to form controls.
   *
   * @param {string} apiKey
   * @returns {HTMLElement | null}
   */
  #resolveFieldByApiKey(apiKey) {
    const normalizedKey = String(apiKey || '').split('.')[0];

    switch (normalizedKey) {
      case 'firstName':
      case 'first_name':
        return this.refs.firstName;
      case 'lastName':
      case 'last_name':
        return this.refs.lastName;
      case 'email':
        return this.refs.email;
      case 'birthDate':
      case 'birth_date':
        return this.refs.dateOfBirth;
      case 'dischargeDate':
      case 'discharge_date':
        return this.refs.form.querySelector('[name="discharge_date"]');
      case 'organization':
      case 'organization_id':
        return this.refs.organizationAutocomplete?.refs?.input || null;
      case 'branch':
      case 'branch_of_service':
        return this.refs.branchAutocomplete?.refs?.input || this.refs.form.querySelector('[name="branch_of_service"]');
      case 'postalCode':
      case 'postal_code':
        return this.refs.postalCode || null;
      case 'files':
      case 'file':
      case 'credential_file':
        return this.refs.fileUpload;
      default:
        return null;
    }
  }

  /**
   * Validates visible required fields in current step.
   *
   * @returns {boolean}
   */
  #validate() {
    let valid = true;
    let firstInvalidField = null;
    const requiredInputs = this.refs.form.querySelectorAll('[required]:not([disabled])');

    for (const field of requiredInputs) {
      if (!this.#isVisibleField(field)) continue;

      const isEmpty = field instanceof HTMLInputElement && field.type === 'file' ? field.files?.length === 0 : !field.value;
      if (isEmpty) {
        this.#setError(field);
        if (!firstInvalidField) firstInvalidField = field;
        valid = false;
        continue;
      }

      if (field instanceof HTMLInputElement && field.name === 'discharge_date' && !this.#normalizeMonthYearForApi(field.value)) {
        this.#setError(field, 'Use mm/yyyy format.');
        if (!firstInvalidField) firstInvalidField = field;
        valid = false;
        continue;
      }

      if (field instanceof HTMLInputElement && field.name === 'date_of_birth' && !this.#normalizeDateForApi(field.value)) {
        this.#setError(field, 'Use mm/dd/yyyy format.');
        if (!firstInvalidField) firstInvalidField = field;
        valid = false;
        continue;
      }

      if (field instanceof HTMLInputElement && !field.validity.valid) {
        this.#setError(field);
        if (!firstInvalidField) firstInvalidField = field;
        valid = false;
        continue;
      }

      if (field instanceof HTMLInputElement && field.type === 'file') {
        const files = [...(field.files ?? [])];
        const fileUploadComponent = field.closest('file-upload-component');
        const invalidIndices = [];
        let fileError = null;

        files.forEach((file, index) => {
          const lastDot = file.name.lastIndexOf('.');
          const extension = lastDot > 0 ? file.name.slice(lastDot + 1).toLowerCase() : '';
          if (!extension || !ALLOWED_FILE_EXTENSIONS.includes(extension)) {
            invalidIndices.push(index);
            if (!fileError) fileError = `Allowed file types: ${ALLOWED_FILE_EXTENSIONS.join(', ')}.`;
            return;
          }
          if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
            invalidIndices.push(index);
            if (!fileError) fileError = 'Maximum file size is 5 MB.';
          }
        });

        fileUploadComponent?.setItemErrors?.(invalidIndices);

        if (fileError) {
          this.#setError(field, fileError);
          if (!firstInvalidField) firstInvalidField = field;
          valid = false;
          continue;
        }
      }

    }

    const organizationInput = this.refs.organizationAutocomplete?.refs?.input;
    const organizationIsVisible = this.#isVisibleField(organizationInput);
    const organizationRequiresValue = organizationInput?.hasAttribute('required');
    const organizationHasValue = Boolean(organizationInput?.value?.trim());
    const organizationHasSelection = this.refs.organizationAutocomplete?.hasSelection?.();

    if (organizationIsVisible && organizationRequiresValue && organizationHasValue && !organizationHasSelection && !this.#allowFreeOrganization) {
      this.#setError(organizationInput, 'Please select an organization from the list.');
      if (!firstInvalidField) firstInvalidField = organizationInput;
      valid = false;
    }

    if (this.refs.branchAutocomplete) {
      const branchInput = this.refs.branchAutocomplete.refs?.input;
      const branchIsVisible = this.#isVisibleField(branchInput);
      const branchRequiresValue = branchInput?.hasAttribute('required');
      const branchHasValue = Boolean(branchInput?.value?.trim());
      const branchHasSelection = this.refs.branchAutocomplete?.hasSelection?.();

      if (branchIsVisible && branchRequiresValue && branchHasValue && !branchHasSelection) {
        this.#setError(branchInput, 'Please select a branch from the list.');
        if (!firstInvalidField) firstInvalidField = branchInput;
        valid = false;
      }
    }

    if (!valid && firstInvalidField instanceof HTMLElement) {
      firstInvalidField.focus();
    }

    return valid;
  }

  /**
   * Normalizes native `YYYY-MM-DD` and `MM/DD/YYYY` user input to `YYYY-MM-DD`
   * for API payload. Returns an empty string when the date is malformed or
   * doesn't exist (e.g. 02/30/2020).
   *
   * @param {string} value
   * @returns {string}
   */
  #normalizeDateForApi(value) {
    const raw = String(value || '').trim();
    const nativeDateMatch = raw.match(/^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);
    const formattedDateMatch = raw.match(/^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/);
    const match = nativeDateMatch || formattedDateMatch;

    if (!match) return '';

    const year = nativeDateMatch ? match[1] : match[3];
    const month = nativeDateMatch ? match[2] : match[1];
    const day = nativeDateMatch ? match[3] : match[2];
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);

    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getFullYear() !== Number(year) ||
      parsed.getMonth() + 1 !== Number(month) ||
      parsed.getDate() !== Number(day)
    ) {
      return '';
    }
    return `${year}-${month}-${day}`;
  }

  /**
   * Formats raw user input as `MM/YYYY`.
   *
   * @param {string} value
   * @returns {string}
   */
  #formatMonthYearInput(value) {
    const raw = String(value || '');
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    if (digits.length < 2) return digits;
    if (digits.length === 2) return raw.includes('/') ? `${digits}/` : digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  /**
   * Formats raw user input as `MM/DD/YYYY`.
   *
   * @param {string} value
   * @returns {string}
   */
  #formatDateInput(value) {
    const raw = String(value || '');
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length < 2) return digits;
    if (digits.length === 2) return raw.includes('/') ? `${digits}/` : digits;
    if (digits.length < 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (digits.length === 4) {
      const slashCount = (raw.match(/\//g) || []).length;
      const trailing = slashCount >= 2 ? '/' : '';
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}${trailing}`;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  /**
   * Maps the caret to the same digit position after applying the mask.
   *
   * @param {string} formattedValue
   * @param {number} digitCount
   * @returns {number}
   */
  #resolveMaskCursor(formattedValue, digitCount) {
    if (digitCount <= 0) return 0;

    let seenDigits = 0;
    for (let index = 0; index < formattedValue.length; index += 1) {
      if (!/\d/.test(formattedValue[index])) continue;
      seenDigits += 1;
      if (seenDigits >= digitCount) return index + 1;
    }

    return formattedValue.length;
  }

  /**
   * Normalizes month/year input to `YYYY-MM-DD` (day defaults to `01`) for API payload.
   *
   * @param {string} value
   * @returns {string}
   */
  #normalizeMonthYearForApi(value) {
    const raw = String(value || '').trim();
    const match = raw.match(/^(0[1-9]|1[0-2])\/(\d{4})$/);
    if (!match) return '';
    return `${match[2]}-${match[1]}-01`;
  }

  /**
   * Checks if a field is currently visible to the user.
   *
   * @param {HTMLElement} input
   * @returns {boolean}
   */
  #isVisibleField(input) {
    return !this.#isHidden(input) && !input.closest('._hidden');
  }

  /**
   * Applies error state to a single field.
   *
   * @param {HTMLElement} input
   * @returns {void}
   */
  #setError(input, message = '') {
    input.setAttribute('aria-invalid', 'true');
    const controlWrapper = input.closest('.text-field, .text-area, .select, .file-upload');
    if (controlWrapper) controlWrapper.classList.add('_error');

    const fieldWrapper = input.closest('.field');
    if (fieldWrapper) fieldWrapper.classList.add('_error');
    const errorElement = fieldWrapper?.querySelector('[data-field-error]');
    if (errorElement instanceof HTMLElement) {
      errorElement.textContent = message || input.validationMessage || 'This field is required.';
      this.#setHidden(errorElement, false);
    }
  }

  /**
   * Clears all field-level validation errors.
   *
   * @returns {void}
   */
  #clearErrors() {
    for (const invalid of this.refs.form.querySelectorAll('[aria-invalid="true"]')) {
      invalid.removeAttribute('aria-invalid');
    }

    for (const errorWrapper of this.refs.form.querySelectorAll('._error')) {
      errorWrapper.classList.remove('_error');
    }

    for (const fieldError of this.refs.form.querySelectorAll('[data-field-error]')) {
      if (!(fieldError instanceof HTMLElement)) continue;
      fieldError.textContent = '';
      this.#setHidden(fieldError, true);
    }
  }

  /**
   * Toggles loading/disabled state for submit controls.
   *
   * @param {boolean} state
   * @returns {void}
   */
  #setSubmitting(state) {
    this.refs.submitButton.classList.toggle('_loading', state);
    this.refs.submitButton.disabled = state;
    this.refs.backButton.disabled = state;
  }

  /**
   * Updates submit button label for current step.
   *
   * @param {string} label
   * @returns {void}
   */
  #setSubmitLabel(label) {
    this.refs.submitDefaultText.textContent = label;
  }

  /**
   * Shows or hides status message text.
   *
   * @param {string} message
   * @param {boolean} isError
   * @returns {void}
   */
  #setStatus(message, isError) {
    if (!message) {
      this.#setHidden(this.refs.statusMessage, true);
      this.refs.statusTitle.textContent = '';
      this.refs.statusText.textContent = '';
      this.refs.statusMessage.classList.remove('alert--error');
      return;
    }

    this.#setHidden(this.refs.statusMessage, false);
    this.refs.statusTitle.textContent = isError ? 'Error' : 'Notice';
    this.refs.statusText.textContent = message;
    this.refs.statusMessage.classList.toggle('alert--error', isError);
    this.refs.statusMessage.classList.toggle('alert--warning', !isError);

    requestAnimationFrame(() => {
      this.refs.statusMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /**
   * Toggles `_hidden` state on an element.
   *
   * @param {HTMLElement | null | undefined} element
   * @param {boolean} state
   * @returns {void}
   */
  #setHidden(element, state) {
    if (!element) return;
    element.classList.toggle('_hidden', Boolean(state));
  }

  /**
   * Checks whether element has `_hidden` modifier.
   *
   * @param {HTMLElement | null | undefined} element
   * @returns {boolean}
   */
  #isHidden(element) {
    return element?.classList?.contains('_hidden');
  }
}

if (!customElements.get('vip-application-form')) {
  customElements.define('vip-application-form', VipApplicationForm);
}
